/**
 * The Surgery checklist definition.
 *
 * Every label and every option value is copied verbatim from legacy
 * `html/html/lib/patient-details/consult-details-checklist.php`. The values are
 * what `consult_requests` stores, so rewording an option stops it matching; the
 * labels are what surgical staff read, so rewording one changes the meaning of
 * a clinical check. Neither is cosmetic.
 */

export type ChecklistFieldKind = 'select' | 'text';

export interface ChecklistOption {
  /** Stored in consult_requests. Never change. */
  value: string;
  /** Shown to staff. */
  label: string;
  /** Legacy renders the row's control but hides this option unless selected. */
  hidden?: boolean;
}

export interface ChecklistField {
  /** The consult_requests column. */
  key: string;
  label: string;
  kind: ChecklistFieldKind;
  options?: ChecklistOption[];
  /**
   * Legacy renders the row with `style="display:none;"`. Kept in the definition
   * because the column is still written and still carries data — hiding is a
   * display decision, not a schema one.
   */
  hiddenRow?: boolean;
}

/** No / Yes / N/A, the shape most of the checklist uses. */
const NO_YES_NA: ChecklistOption[] = [
  { value: '0', label: 'No' },
  { value: '1', label: 'Yes' },
  { value: '2', label: 'N/A' },
];

const NO_YES: ChecklistOption[] = [
  { value: '0', label: 'No' },
  { value: '1', label: 'Yes' },
];

export const CHECKLIST_FIELDS: ChecklistField[] = [
  {
    key: 'labsReviewed',
    label: 'Labs Checked',
    kind: 'select',
    options: [
      { value: 'No', label: 'Not Reviewed' },
      { value: 'Yes', label: 'Reviewed' },
      { value: 'N/A', label: 'N/A' },
    ],
  },
  {
    key: 'clearance',
    label: 'Medical Clearance',
    kind: 'select',
    options: [
      { value: '', label: 'Select one...' },
      { value: 'Not Needed', label: 'Not Needed' },
      { value: 'Required', label: 'Required', hidden: true },
      { value: 'In Process', label: 'In Process' },
      { value: 'Further Workup', label: 'Further Workup', hidden: true },
      { value: 'Cleared', label: 'Cleared' },
      { value: 'Not Cleared', label: 'Not Cleared' },
      { value: 'Postponed', label: 'Postponed' },
    ],
  },
  {
    key: 'mammogram',
    label: 'Mammogram',
    kind: 'select',
    options: [
      { value: '0', label: 'No' },
      { value: '1', label: 'Yes' },
      { value: '2', label: 'N/A' },
      { value: '3', label: 'In Process' },
      { value: '4', label: 'Declined' },
    ],
  },
  { key: 'consentsSigned', label: 'Consents Signed', kind: 'select', options: NO_YES },
  { key: 'releases', label: 'Releases', kind: 'select', options: NO_YES_NA, hiddenRow: true },
  {
    key: 'additionalConsent',
    label: 'Additional Consent',
    kind: 'select',
    options: NO_YES_NA,
    hiddenRow: true,
  },
  { key: 'implantOrder', label: 'Implant Order', kind: 'select', options: NO_YES_NA },
  { key: 'specialOrder', label: 'Special Order', kind: 'select', options: NO_YES_NA },
  { key: 'photosVerified', label: 'Photos Verified', kind: 'select', options: NO_YES },
  { key: 'warrantyPapers', label: 'Warranty Papers', kind: 'select', options: NO_YES_NA },
  { key: 'rxSent', label: 'RX Sent', kind: 'select', options: NO_YES_NA },
  { key: 'highBMIRelease', label: 'High BMI Release', kind: 'select', options: NO_YES_NA },
  { key: 'smokersRelease', label: 'Smokers Release', kind: 'select', options: NO_YES_NA },
  {
    key: 'difficultCaseRelease',
    label: 'Difficult Case Release',
    kind: 'select',
    options: NO_YES_NA,
  },
  { key: 'noChargeRelease', label: 'No Charge Release', kind: 'select', options: NO_YES_NA },
  { key: 'highBPRelease', label: 'High BP Release', kind: 'select', options: NO_YES_NA },
  {
    key: 'medicationRecon',
    label: 'Medication Reconciliation',
    kind: 'select',
    options: NO_YES_NA,
  },
  { key: 'garmentsOrdered', label: 'Garments Ordered', kind: 'select', options: NO_YES_NA },
];

/** The implant free-text pair, rendered under its own "Implants Chosen" heading. */
export const IMPLANT_FIELDS: ChecklistField[] = [
  { key: 'leftImplant', label: 'Left', kind: 'text' },
  { key: 'rightImplant', label: 'Right', kind: 'text' },
];

/** Consult status, including the option legacy restricts to senior staff. */
export const CONSULT_STATUS_CHOICES: ChecklistOption[] = [
  { value: '', label: 'Select one...' },
  { value: 'Pre-Consult', label: 'Pre-Consult' },
  { value: 'Consult Ready', label: 'Consult Ready' },
  { value: 'Approved', label: 'Approved' },
  { value: 'QBS Required', label: 'QBS Required' },
  { value: 'Virtual Consult', label: 'Virtual Consult' },
  { value: 'Not Approved', label: 'Not Approved' },
  { value: 'Complete', label: 'Complete', hidden: true },
];

export const CRNA_CLEARANCE_CHOICES: ChecklistOption[] = [
  { value: '', label: 'Select one...' },
  { value: 'In Process', label: 'In Process' },
  { value: 'Cleared', label: 'Cleared' },
  { value: 'Not Cleared', label: 'Not Cleared' },
];

/**
 * Which options to render for a field, given the value currently stored.
 * A hidden option stays hidden unless it is the current value — otherwise
 * selecting it would be impossible AND the control would show a blank for a
 * record that legitimately holds it.
 */
export function visibleOptions(
  options: ChecklistOption[] | undefined,
  currentValue: string,
): ChecklistOption[] {
  if (!options) return [];
  return options.filter((o) => !o.hidden || o.value === currentValue);
}

/** The four free-text notes, with the headings legacy gives them. */
export const NOTE_FIELDS = [
  { key: 'medicalClearanceNote', label: 'Medical Clearance Note' },
  { key: 'CRNANote', label: 'CRNA Clearance Note' },
  { key: 'surgeonsNote', label: "Surgeon's Note" },
] as const;

/**
 * `coordinatorsNote` packs three values into one column as
 * `note|BEGINSUBJECTLINE|subject|BEGINZOOMLINK|zoom`, the order legacy writes
 * (html/html/consult_update_coordinators_note.php:33). Legacy still reads the
 * column, so the encoding must round-trip byte for byte.
 */
export const COORDINATOR_NOTE_ZOOM_SEPARATOR = '|BEGINZOOMLINK|';
export const COORDINATOR_NOTE_SUBJECT_SEPARATOR = '|BEGINSUBJECTLINE|';

export interface CoordinatorNote {
  note: string;
  zoomLink: string;
  subject: string;
}

/** Text before the first `separator`, and everything after it. */
function splitOnce(raw: string, separator: string): [string, string | undefined] {
  const at = raw.indexOf(separator);
  if (at === -1) return [raw, undefined];
  return [raw.slice(0, at), raw.slice(at + separator.length)];
}

function countOf(raw: string, separator: string): number {
  return raw.split(separator).length - 1;
}

/**
 * Reads the column the way legacy does
 * (html/html/lib/patient-details/consult-details-coordinators-note.php:70-83):
 * split on the zoom marker first, then split the part before it on the subject
 * marker.
 *
 * Two deliberate departures from legacy:
 *
 *  - Before the order was fixed, V5 wrote `note|BEGINZOOMLINK|zoom|BEGINSUBJECTLINE|subject`.
 *    A value with exactly one of each marker, zoom first, is read in that order,
 *    so staging rows show the right fields until the repair SQL
 *    (api/database/2026_09_23_surgery_coordinator_note_order_repair.sql)
 *    re-packs them, and a save writes them back in legacy order.
 *  - Legacy's explode() keeps only the piece between the first and second
 *    marker, so a value with a repeated marker loses text when legacy re-saves
 *    it. Here each split keeps everything after the FIRST marker, so a re-save
 *    loses no text. Values with repeated markers are listed by the repair SQL
 *    for hand review.
 */
export function parseCoordinatorNote(raw: string): CoordinatorNote {
  const zoomAt = raw.indexOf(COORDINATOR_NOTE_ZOOM_SEPARATOR);
  const subjectAt = raw.indexOf(COORDINATOR_NOTE_SUBJECT_SEPARATOR);
  const isOldV5Order =
    zoomAt !== -1 &&
    subjectAt > zoomAt &&
    countOf(raw, COORDINATOR_NOTE_ZOOM_SEPARATOR) === 1 &&
    countOf(raw, COORDINATOR_NOTE_SUBJECT_SEPARATOR) === 1;

  if (isOldV5Order) {
    const [note, afterZoom = ''] = splitOnce(raw, COORDINATOR_NOTE_ZOOM_SEPARATOR);
    const [zoomLink, subject = ''] = splitOnce(afterZoom, COORDINATOR_NOTE_SUBJECT_SEPARATOR);
    return { note, zoomLink, subject };
  }

  const [beforeZoom, zoomLink = ''] = splitOnce(raw, COORDINATOR_NOTE_ZOOM_SEPARATOR);
  const [note, subject = ''] = splitOnce(beforeZoom, COORDINATOR_NOTE_SUBJECT_SEPARATOR);

  return { note, zoomLink, subject };
}

export function formatCoordinatorNote(parts: CoordinatorNote): string {
  return (
    parts.note +
    COORDINATOR_NOTE_SUBJECT_SEPARATOR +
    parts.subject +
    COORDINATOR_NOTE_ZOOM_SEPARATOR +
    parts.zoomLink
  );
}
